import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select } from '../components/ui';
import { AddressForm } from '../components/forms/AddressForm';
import { DynamicTable, Column } from '../components/forms/DynamicTable';
import { FormSection } from '../components/layout';
import { maskCNPJ, maskCPFOrCNPJ, maskCNAE, maskCurrency, maskPhone, maskDate, dateFromISO, dateToISO, unmaskCurrency } from '../utils/masks';
import { pickErrors } from '../utils/formValidation';
import {
  PessoaJuridicaForm as PessoaJuridicaFormData,
  CompanyIdentification,
  FinancialInfo,
  Shareholder,
  FinalBeneficiary,
  Address,
} from '../types';

export interface PessoaJuridicaFormProps {
  data: Partial<PessoaJuridicaFormData>;
  onChange: (data: Partial<PessoaJuridicaFormData>) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

export const PessoaJuridicaForm: React.FC<PessoaJuridicaFormProps> = ({
  data,
  onChange,
  errors = {},
  disabled = false,
}) => {
  const { t } = useTranslation();

  const countryOptions = useMemo(() => [
    { value: 'BR', label: t('pessoaJuridica.countryOptions.BR') },
    { value: 'US', label: t('pessoaJuridica.countryOptions.US') },
    { value: 'AR', label: t('pessoaJuridica.countryOptions.AR') },
    { value: 'PY', label: t('pessoaJuridica.countryOptions.PY') },
    { value: 'UY', label: t('pessoaJuridica.countryOptions.UY') },
    { value: 'OTHER', label: t('pessoaJuridica.countryOptions.OTHER') },
  ], [t]);

  // Handle company identification changes
  const handleIdentificationChange = useCallback(
    (field: keyof CompanyIdentification) => (value: string) => {
      onChange({
        ...data,
        identification: {
          ...data.identification,
          [field]: value,
        } as CompanyIdentification,
      });
    },
    [data, onChange]
  );

  // Handle headquarters address changes
  const handleHeadquartersAddressChange = useCallback(
    (address: Partial<Address>) => {
      onChange({
        ...data,
        headquartersAddress: address as Address,
      });
    },
    [data, onChange]
  );

  // Handle contact changes
  const handleContactChange = useCallback(
    (field: 'contactPerson' | 'contactPhone') => (value: string) => {
      onChange({
        ...data,
        [field]: value,
      });
    },
    [data, onChange]
  );

  // Handle financial info changes
  const handleFinancialChange = useCallback(
    (field: keyof FinancialInfo) => (value: string) => {
      let parsedValue: string | number = value;

      if (['capital', 'annualRevenue', 'netWorth'].includes(field)) {
        parsedValue = unmaskCurrency(value);
      }

      onChange({
        ...data,
        financial: {
          ...data.financial,
          [field]: parsedValue,
        } as FinancialInfo,
      });
    },
    [data, onChange]
  );

  // Handle shareholders changes
  const handleShareholdersChange = useCallback(
    (shareholders: Shareholder[]) => {
      onChange({
        ...data,
        shareholders,
      });
    },
    [data, onChange]
  );

  // Handle beneficiaries changes
  const handleBeneficiariesChange = useCallback(
    (finalBeneficiaries: FinalBeneficiary[]) => {
      onChange({
        ...data,
        finalBeneficiaries,
      });
    },
    [data, onChange]
  );

  // Create new shareholder
  const createNewShareholder = (): Shareholder => ({
    id: `shareholder-${Date.now()}`,
    name: '',
    cpfCnpj: '',
    sharePercentage: 0,
  });

  // Create new beneficiary
  const createNewBeneficiary = (): FinalBeneficiary => ({
    id: `beneficiary-${Date.now()}`,
    name: '',
    cpf: '',
    sharePercentage: 0,
  });

  // Shareholder table columns
  const shareholderColumns: Column<Shareholder>[] = useMemo(() => [
    {
      key: 'name',
      header: t('pessoaJuridica.shareholderName'),
      width: '40%',
      render: (item, _index, onChange) => (
        <Input
          value={item.name}
          onChange={(value) => onChange({ ...item, name: value })}
          placeholder={t('pessoaJuridica.shareholderNamePlaceholder')}
          disabled={disabled}
          containerClassName="mb-0"
        />
      ),
    },
    {
      key: 'cpfCnpj',
      header: t('pessoaJuridica.shareholderCpfCnpj'),
      width: '35%',
      render: (item, _index, onChange) => (
        <Input
          value={item.cpfCnpj}
          onChange={(value) => onChange({ ...item, cpfCnpj: maskCPFOrCNPJ(value) })}
          placeholder={t('pessoaJuridica.shareholderCpfCnpjPlaceholder')}
          disabled={disabled}
          containerClassName="mb-0"
        />
      ),
    },
    {
      key: 'sharePercentage',
      header: t('pessoaJuridica.shareholderPercentage'),
      width: '25%',
      render: (item, _index, onChange) => (
        <Input
          value={item.sharePercentage?.toString() || ''}
          onChange={(value) => onChange({ ...item, sharePercentage: parseFloat(value) || 0 })}
          placeholder="0.00"
          type="number"
          disabled={disabled}
          containerClassName="mb-0"
        />
      ),
    },
  ], [t, disabled]);

  // Beneficiary table columns
  const beneficiaryColumns: Column<FinalBeneficiary>[] = useMemo(() => [
    {
      key: 'name',
      header: t('pessoaJuridica.beneficiaryName'),
      width: '40%',
      render: (item, _index, onChange) => (
        <Input
          value={item.name}
          onChange={(value) => onChange({ ...item, name: value })}
          placeholder={t('pessoaJuridica.beneficiaryNamePlaceholder')}
          disabled={disabled}
          containerClassName="mb-0"
        />
      ),
    },
    {
      key: 'cpf',
      header: t('pessoaJuridica.beneficiaryCpf'),
      width: '35%',
      render: (item, _index, onChange) => (
        <Input
          value={item.cpf}
          onChange={(value) => onChange({ ...item, cpf: maskCPFOrCNPJ(value) })}
          placeholder={t('pessoaJuridica.beneficiaryCpf')}
          disabled={disabled}
          containerClassName="mb-0"
        />
      ),
    },
    {
      key: 'sharePercentage',
      header: t('pessoaJuridica.beneficiaryPercentage'),
      width: '25%',
      render: (item, _index, onChange) => (
        <Input
          value={item.sharePercentage?.toString() || ''}
          onChange={(value) => onChange({ ...item, sharePercentage: parseFloat(value) || 0 })}
          placeholder="0.00"
          type="number"
          disabled={disabled}
          containerClassName="mb-0"
        />
      ),
    },
  ], [t, disabled]);

  return (
    <div className="space-y-6">
      {/* Section 1: Company Identification */}
      <FormSection
        title={t('pessoaJuridica.sectionIdentification')}
        description={t('pessoaJuridica.sectionIdentificationDesc')}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input
              label={t('pessoaJuridica.companyName')}
              value={data.identification?.companyName || ''}
              onChange={handleIdentificationChange('companyName')}
              placeholder={t('pessoaJuridica.companyNamePlaceholder')}
              error={errors['identification.companyName']}
              required
              disabled={disabled}
            />
          </div>

          <Input
            label={t('pessoaJuridica.cnpj')}
            value={data.identification?.cnpj || ''}
            onChange={handleIdentificationChange('cnpj')}
            mask={maskCNPJ}
            placeholder="00.000.000/0000-00"
            error={errors['identification.cnpj']}
            required
            disabled={disabled}
          />

          <Input
            label={t('pessoaJuridica.constitutionDate')}
            value={dateFromISO(data.identification?.constitutionDate || '')}
            onChange={(value) => {
              const masked = maskDate(value);
              const iso = dateToISO(masked);
              handleIdentificationChange('constitutionDate')(iso);
            }}
            placeholder="DD/MM/AAAA"
            error={errors['identification.constitutionDate']}
            required
            disabled={disabled}
          />

          <Input
            label={t('pessoaJuridica.mainActivity')}
            value={data.identification?.mainActivity || ''}
            onChange={handleIdentificationChange('mainActivity')}
            placeholder={t('pessoaJuridica.mainActivityPlaceholder')}
            error={errors['identification.mainActivity']}
            required
            disabled={disabled}
          />

          <Input
            label={t('pessoaJuridica.cnaeCode')}
            value={data.identification?.cnaeCode || ''}
            onChange={handleIdentificationChange('cnaeCode')}
            mask={maskCNAE}
            placeholder="0000-0/00"
            error={errors['identification.cnaeCode']}
            required
            disabled={disabled}
          />
        </div>
      </FormSection>

      {/* Section 2: Headquarters Address */}
      <FormSection
        title={t('pessoaJuridica.sectionHeadquartersAddress')}
        description={t('pessoaJuridica.sectionHeadquartersAddressDesc')}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
      >
        <AddressForm
          data={data.headquartersAddress || {}}
          onChange={handleHeadquartersAddressChange}
          errors={pickErrors(errors, 'headquartersAddress')}
          showPhones={false}
          showPoBox={true}
          disabled={disabled}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Input
            label={t('pessoaJuridica.contactPhone')}
            value={data.contactPhone || ''}
            onChange={handleContactChange('contactPhone')}
            mask={maskPhone}
            placeholder="(00) 0000-0000"
            error={errors['contactPhone']}
            required
            disabled={disabled}
          />

          <Input
            label={t('pessoaJuridica.contactPerson')}
            value={data.contactPerson || ''}
            onChange={handleContactChange('contactPerson')}
            placeholder={t('pessoaJuridica.contactPersonPlaceholder')}
            error={errors['contactPerson']}
            required
            disabled={disabled}
          />
        </div>
      </FormSection>

      {/* Section 3: Financial Information */}
      <FormSection
        title={t('pessoaJuridica.sectionFinancial')}
        description={t('pessoaJuridica.sectionFinancialDesc')}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('pessoaJuridica.capital')}
            value={data.financial?.capital ? maskCurrency(data.financial.capital, 'BRL') : ''}
            onChange={handleFinancialChange('capital')}
            placeholder="R$ 0,00"
            error={errors['financial.capital']}
            required
            disabled={disabled}
          />

          <Select
            label={t('pessoaJuridica.country')}
            value={data.financial?.country || ''}
            onChange={handleFinancialChange('country')}
            options={countryOptions}
            error={errors['financial.country']}
            required
            disabled={disabled}
          />

          <Input
            label={t('pessoaJuridica.annualRevenue')}
            value={data.financial?.annualRevenue ? maskCurrency(data.financial.annualRevenue, 'BRL') : ''}
            onChange={handleFinancialChange('annualRevenue')}
            placeholder="R$ 0,00"
            error={errors['financial.annualRevenue']}
            required
            disabled={disabled}
          />

          <Input
            label={t('pessoaJuridica.netWorth')}
            value={data.financial?.netWorth ? maskCurrency(data.financial.netWorth, 'BRL') : ''}
            onChange={handleFinancialChange('netWorth')}
            placeholder="R$ 0,00"
            error={errors['financial.netWorth']}
            required
            disabled={disabled}
          />
        </div>
      </FormSection>

      {/* Section 4: Shareholders */}
      <FormSection
        title={t('pessoaJuridica.sectionShareholders')}
        description={t('pessoaJuridica.sectionShareholdersDesc')}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        }
      >
        <DynamicTable
          columns={shareholderColumns}
          data={data.shareholders || []}
          onChange={handleShareholdersChange}
          createNewItem={createNewShareholder}
          minRows={1}
          maxRows={10}
          addButtonText={t('pessoaJuridica.addShareholder')}
          disabled={disabled}
          error={errors['shareholders']}
        />

        {data.shareholders && data.shareholders.length > 0 && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>{t('pessoaJuridica.totalSharePercentage')}</strong>{' '}
              {data.shareholders.reduce((acc, s) => acc + (s.sharePercentage || 0), 0).toFixed(2)}%
            </p>
            {data.shareholders.reduce((acc, s) => acc + (s.sharePercentage || 0), 0) !== 100 && (
              <p className="text-sm text-amber-600 mt-1">
                {t('pessoaJuridica.sharePercentageWarning')}
              </p>
            )}
          </div>
        )}
      </FormSection>

      {/* Section 5: Final Beneficiaries */}
      <FormSection
        title={t('pessoaJuridica.sectionBeneficiaries')}
        description={t('pessoaJuridica.sectionBeneficiariesDesc')}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        }
      >
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800" dangerouslySetInnerHTML={{ __html: t('pessoaJuridica.beneficiaryDefinition') }} />
        </div>

        <DynamicTable
          columns={beneficiaryColumns}
          data={data.finalBeneficiaries || []}
          onChange={handleBeneficiariesChange}
          createNewItem={createNewBeneficiary}
          minRows={0}
          maxRows={10}
          addButtonText={t('pessoaJuridica.addBeneficiary')}
          disabled={disabled}
          error={errors['finalBeneficiaries']}
        />
      </FormSection>
    </div>
  );
};

export default PessoaJuridicaForm;
